import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

// @mui material components
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Context
import { useMaterialUIController } from "context";

// Network
import { request } from "app/network";

// Components
import { GenericListScreen, Column } from "components/GenericListScreen";
import AddTenantModal from "./components/AddTenantModal/AddTenantModal";
import EditTenantModal from "./components/EditTenantModal/EditTenantModal";

function TenantListScreen() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const queryClient = useQueryClient();

    // State
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Handlers
    const refreshList = () => {
        queryClient.invalidateQueries({ queryKey: ['api', 'Tenant.pagedList'] });
    };

    const handleEditClick = (tenant: any) => {
        setSelectedTenant(tenant);
        setIsEditModalOpen(true);
    };

    const handleDeleteClick = (tenant: any) => {
        setSelectedTenant(tenant);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedTenant) return;

        try {
            setDeleteLoading(true);
            setDeleteError(null);
            await request('Tenant.delete', {
                pathParams: { id: selectedTenant.seqId }
            });
            setDeleteLoading(false);
            setIsDeleteDialogOpen(false);
            setSelectedTenant(null);
            refreshList();
        } catch (error: any) {
            console.error('Delete Tenant Error:', error);
            setDeleteLoading(false);
            setDeleteError(error.message || '테넌트 삭제 중 오류가 발생했습니다.');
        }
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setSelectedTenant(null);
        setDeleteError(null);
    };

    // Columns Definition
    const columns: Column[] = useMemo(() => [
        { Header: "ID", accessor: "seqId", align: "left", width: "10%" },
        {
            Header: "Name",
            accessor: "name",
            align: "left",
            width: "20%",
            Cell: ({ value }: any) => (
                <MDTypography variant="button" fontWeight="medium">
                    {value}
                </MDTypography>
            )
        },
        { Header: "Description", accessor: "description", align: "left", width: "30%" },
        {
            Header: "Status",
            accessor: "status",
            align: "center",
            width: "15%",
            Cell: ({ value }: any) => (
                <Chip
                    label={value}
                    color={value === "ACTIVE" ? "success" : "default"}
                    size="small"
                    sx={{ fontWeight: "medium" }}
                />
            )
        },
        { Header: "Created", accessor: "created", align: "center", width: "15%" },
        {
            Header: "Actions",
            accessor: "actions",
            align: "center",
            width: "10%",
            disableSortBy: true,
            Cell: ({ row }: any) => (
                <MDBox display="flex" justifyContent="center">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditClick(row.original); }} color="info">
                        <Icon>edit</Icon>
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteClick(row.original); }} color="error">
                        <Icon>delete</Icon>
                    </IconButton>
                </MDBox>
            )
        }
    ], []);

    const dataTransform = (data: any) => {
        const items = (data.items || []).map((tenant: any) => ({
            seqId: tenant.seqId,
            name: tenant.name || "-",
            description: tenant.description || "",
            status: tenant.status || (tenant.isActive ? "ACTIVE" : "INACTIVE"),
            // Store original object for actions
            original: tenant,
            // Format Date
            created: tenant.crtDt ? new Date(tenant.crtDt).toLocaleDateString() : "-",
        }));
        return { items, totalCount: data.totalCount || 0 };
    };

    return (
        <>
            <GenericListScreen
                title="테넌트 목록 ( TENANT LIST )"
                apiName="Tenant.pagedList"
                columns={columns}
                dataTransform={dataTransform}
                /* Passing null onRowClick to start with, unelss we want row click to edit? 
                   Existing impl had handleTenantClick which did console.log only.
                   We have explicit actions now.
                */
                actionComponent={
                    <MDButton
                        variant="gradient"
                        color="info"
                        onClick={() => setIsAddModalOpen(true)}
                    >
                        <Icon fontSize="small" sx={{ mr: 0.5 }}>add</Icon>
                        테넌트 추가
                    </MDButton>
                }
            />

            {/* Modals */}
            <AddTenantModal
                open={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={() => { setIsAddModalOpen(false); refreshList(); }}
                darkMode={darkMode}
            />

            <EditTenantModal
                open={isEditModalOpen}
                onClose={() => { setIsEditModalOpen(false); setSelectedTenant(null); }}
                onSuccess={() => { setIsEditModalOpen(false); refreshList(); }}
                tenant={selectedTenant}
                darkMode={darkMode}
            />

            {/* Delete Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? '#1a2035' : '#fff',
                        backgroundImage: 'none'
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? '#fff' : '#000' }}>
                    테넌트 삭제 확인
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                        "{selectedTenant?.name}" 테넌트를 삭제하시겠습니까?<br />
                        이 작업은 취소할 수 없습니다.
                    </DialogContentText>
                    {deleteError && (
                        <MDBox mt={2}>
                            <p style={{ color: '#f44336', fontSize: '0.875rem', margin: 0 }}>
                                {deleteError}
                            </p>
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleCloseDeleteDialog} color="secondary">
                        취소
                    </MDButton>
                    <MDButton
                        onClick={handleConfirmDelete}
                        variant="gradient"
                        color="error"
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? '삭제 중...' : '삭제'}
                    </MDButton>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default TenantListScreen;
